import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import {
    ArrowLeft, Loader2, Save, CheckCircle2, AlertCircle, Tag,
} from "lucide-react";
import { ImageUpload } from "../../components/ui/image-upload";
import { productsService, type Product } from "../../../lib/services/productsService";
import type { Models } from "appwrite";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";

type ProductDoc = Product & Models.Document;

const CATEGORIES = [
    "Air Conditioning",
    "Heat Pump",
    "Refrigeration",
    "Electrical & PLC",
    "Refrigerant ISO Tank",
];

const BADGES = ["Best Seller", "Popular", "New", "Top Rated", "Pro Choice", "Eco Pick", "Innovation"];

const EMPTY: Omit<Product, "$id"> = {
    name: "",
    category: CATEGORIES[0],
    brand: "",
    status: "Active",
    description: "",
    features: "",
    imageUrl: "",
    badge: "",
};

export function AdminProductEditor() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = !id || id === "new";

    const [form, setForm] = useState<Omit<Product, "$id">>(EMPTY);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        productsService.list()
            .then((products) => {
                const p = (products as ProductDoc[]).find((x) => x.$id === id);
                if (p) {
                    setForm({
                        name: p.name,
                        category: p.category,
                        brand: p.brand,
                        status: p.status,
                        description: p.description ?? "",
                        features: p.features ?? "",
                        imageUrl: p.imageUrl ?? "",
                        badge: p.badge ?? "",
                    });
                }
            })
            .catch(() => showToast("error", "Failed to load product."))
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const set = (k: keyof typeof form, v: string) =>
        setForm((f) => ({ ...f, [k]: v }));

    const showToast = (type: "success" | "error", msg: string) => {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 3500);
    };

    const handleSave = async () => {
        if (!form.name.trim()) { showToast("error", "Product name is required."); return; }
        if (!form.brand.trim()) { showToast("error", "Brand is required."); return; }
        setSaving(true);
        try {
            if (isNew) {
                await productsService.create(form);
                showToast("success", "Product added successfully.");
            } else {
                await productsService.update(id!, form);
                showToast("success", "Changes saved.");
            }
            setTimeout(() => navigate("/admin/products"), 1200);
        } catch (e: any) {
            showToast("error", e?.message ?? "Failed to save product.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const featList = form.features
        ? form.features.split(",").map((f) => f.trim()).filter(Boolean)
        : [];

    return (
        <div className="w-full min-w-0">
            {/* ── Sticky top bar ── */}
            <div className="sticky top-0 z-10 bg-background border-b border-border">
                <div className="flex items-center gap-3 px-4 sm:px-6 h-14">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/admin/products")}
                        className="gap-1.5 text-muted-foreground hover:text-white -ml-1 shadow-none">
                        <ArrowLeft className="w-4 h-4" /> Products
                    </Button>
                    <div className="h-4 w-px bg-border" />
                    <span className="text-sm font-medium text-foreground truncate flex-1">
                        {form.name || (isNew ? "New Product" : "Edit Product")}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5 shadow-none">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            {isNew ? "Add Product" : "Save"}
                        </Button>
                    </div>
                </div>
                {toast && (
                    <div className={`flex items-center gap-2 px-6 py-2 text-sm border-t ${toast.type === "success" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-destructive/5 text-destructive border-destructive/10"}`}>
                        {toast.type === "success"
                            ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                            : <AlertCircle className="w-3.5 h-3.5 shrink-0" />}
                        {toast.msg}
                    </div>
                )}
            </div>

            {/* ── Two-column body: Content LEFT, Settings RIGHT ── */}
            <div className="flex flex-col lg:flex-row min-h-[calc(100vh-3.5rem)]">

                {/* ── LEFT: Main product details ── */}
                <div className="flex-1 p-4 sm:p-6 space-y-5 border-r-0 lg:border-r border-border">

                    <div className="space-y-1.5">
                        <Label htmlFor="p-name" className="text-xs font-medium text-muted-foreground">Product Name *</Label>
                        <Input
                            id="p-name"
                            placeholder="e.g. Commercial VRV/VRF Split System"
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            className="shadow-none text-base font-medium h-10"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="p-desc" className="text-xs font-medium text-muted-foreground">Description</Label>
                        <Textarea
                            id="p-desc"
                            rows={6}
                            placeholder="Describe the product, its applications, and key selling points…"
                            value={form.description}
                            onChange={(e) => set("description", e.target.value)}
                            className="resize-y shadow-none min-h-[140px]"
                        />
                    </div>

                    <div className="border-t border-border" />

                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="p-feat" className="text-xs font-medium text-muted-foreground">Features <span className="font-normal">(comma-separated)</span></Label>
                            <span className="text-[11px] text-muted-foreground">{featList.length} bullet{featList.length !== 1 ? "s" : ""}</span>
                        </div>
                        <Input
                            id="p-feat"
                            placeholder="Multi-zone control, Inverter compressor, BMS compatible"
                            value={form.features}
                            onChange={(e) => set("features", e.target.value)}
                            className="shadow-none"
                        />
                        {featList.length > 0 && (
                            <ul className="mt-2 space-y-1">
                                {featList.map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <p className="text-[11px] text-muted-foreground">Displayed as bullet points on the product card.</p>
                    </div>
                </div>

                {/* ── RIGHT: Settings sidebar ── */}
                <div className="w-full lg:w-72 xl:w-80 shrink-0 p-4 sm:p-6 space-y-5 border-t lg:border-t-0 border-border bg-muted/20">

                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Product Settings</p>

                    {/* Status + Badge */}
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Status</Label>
                            <Select value={form.status} onValueChange={(v) => set("status", v)}>
                                <SelectTrigger className="shadow-none h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Active">Active</SelectItem>
                                    <SelectItem value="Inactive">Inactive</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Badge <span className="font-normal">(optional)</span></Label>
                            <Select value={form.badge || "none"} onValueChange={(v) => set("badge", v === "none" ? "" : v)}>
                                <SelectTrigger className="shadow-none h-9"><SelectValue placeholder="None" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {BADGES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Category + Brand */}
                    <div className="space-y-3 border-t border-border pt-4">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Category *</Label>
                            <Select value={form.category} onValueChange={(v) => set("category", v)}>
                                <SelectTrigger className="shadow-none h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="p-brand" className="text-xs font-medium text-muted-foreground">Brand *</Label>
                            <Input id="p-brand" placeholder="e.g. Daikin" value={form.brand}
                                onChange={(e) => set("brand", e.target.value)} className="shadow-none h-9" />
                        </div>
                    </div>

                    {/* Cover Image */}
                    <div className="space-y-2 border-t border-border pt-4">
                        <ImageUpload
                            label="Product Image"
                            value={form.imageUrl}
                            onChange={(url) => set("imageUrl", url)}
                            aspectRatio="video"
                        />
                    </div>

                    {/* Preview badges */}
                    <div className="pt-2 border-t border-border flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={`text-xs ${form.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-muted text-muted-foreground"}`}>
                            {form.status}
                        </Badge>
                        {form.badge && (
                            <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20 gap-1">
                                <Tag className="w-3 h-3" />{form.badge}
                            </Badge>
                        )}
                        <Badge variant="outline" className="text-xs">{form.category}</Badge>
                    </div>
                </div>
            </div>
        </div>
    );
}
